import React, { memo } from 'react'
import { getSmoothStepPath, EdgeLabelRenderer, useInternalNode, EdgeProps, Edge, Position, BaseEdge, useStore } from '@xyflow/react'
import { Box, Paper, Typography } from '@mui/material'
import intl from 'components/i18n/ReactIntlWrapper'
import { getEdgeParams } from '../utils-ts';
import MuiIconDisplay from 'components/MuiIconPicker/MuiIconDisplay';

export type FloatingEdgeType = Edge<{
    actionName: string
    labelKey: string
    extraAttributes: any,
    AllowComments: boolean,
    Icon: string
    DisplayColor: string, 
    scope: number|null
    transitionType?: 'success' | 'error' // Add transition type for automatic actions
}, 'floating'>;

const FloatingEdge = memo((props: EdgeProps<FloatingEdgeType>) => {
    const { id, source, target, markerEnd, style, data } = props;
    
    // Get the full edge from the store to access sourceHandle and targetHandle
    const edge = useStore((store) => store.edges.find((e) => e.id === id));
    const sourceHandle = edge?.sourceHandle;
    const targetHandle = edge?.targetHandle;
    
    const sourceNode = useInternalNode(source);
    const targetNode = useInternalNode(target);
    
    if (!sourceNode || !targetNode) {
        return null
    }

    console.log('FloatingEdge - sourceHandle from edge:', sourceHandle, 'targetHandle from edge:', targetHandle);
    
    // Determine edge color based on source handle or data
    const isSuccessTransition = sourceHandle === 'success' || data?.transitionType === 'success';
    const isErrorTransition = sourceHandle === 'error' || data?.transitionType === 'error';
    
    let edgeColor = data?.DisplayColor || '#b1b1b7';
    if (isSuccessTransition) {
        edgeColor = '#4caf50'; // Green for success
    } else if (isErrorTransition) {
        edgeColor = '#f44336'; // Red for error
    }

    // Get the original edge parameters
    const { sx, sy, tx, ty } = getEdgeParams(sourceNode, targetNode);
    
    // Force specific connection points:
    // - Source (outgoing): always connect from RIGHT of source node
    // - Target (incoming): always connect to LEFT of target node
    const sourcePos = Position.Right;
    const targetPos = Position.Left;
    
    // Calculate the actual connection points based on handle positions
    let sourceX = sourceNode.internals.positionAbsolute.x + sourceNode.measured.width; // Right edge
    let sourceY = sourceNode.internals.positionAbsolute.y + sourceNode.measured.height / 2; // Default: Middle
    
    // Try to get the exact handle position from node internals
    const sourceHandleBounds = sourceNode.internals?.handleBounds?.source;
    console.log('sourceHandle:', sourceHandle, 'sourceHandleBounds:', sourceHandleBounds);
    
    if (sourceHandleBounds && sourceHandle) {
        const handle = sourceHandleBounds.find((h: any) => h.id === sourceHandle);
        console.log('Found handle:', handle);
        if (handle) {
            // Use the exact handle position
            sourceX = sourceNode.internals.positionAbsolute.x + handle.x + (handle.width || 0) / 2;
            sourceY = sourceNode.internals.positionAbsolute.y + handle.y + (handle.height || 0) / 2;
            console.log('Using handle position - sourceX:', sourceX, 'sourceY:', sourceY);
        }
    }
    
    const targetX = targetNode.internals.positionAbsolute.x; // Left edge
    const targetY = targetNode.internals.positionAbsolute.y + targetNode.measured.height / 2; // Middle

    // Create smooth step path with forced connection points
    const [edgePath, labelX, labelY] = getSmoothStepPath({
        sourceX,
        sourceY,
        sourcePosition: sourcePos,
        targetX,
        targetY,
        targetPosition: targetPos,
    })

    FloatingEdge.displayName = 'FloatingEdge'

    // Make edges thin and clean
    const edgeStyle = {
        ...style,
        strokeWidth: 2, // Slightly thicker for colored edges
        stroke: edgeColor,
    };

    return (
        <>
            {/* Custom bigger arrow marker definition */}
            <defs>
                <marker
                    id={`small-arrow-${id}`}
                    viewBox="0 0 12 12"
                    refX="10"
                    refY="6"
                    markerWidth="8"
                    markerHeight="8"
                    orient="auto"
                >
                    <path
                        d="M 0 0 L 0 12 L 12 6 z"
                        fill={edgeColor}
                        stroke={edgeColor}
                    />
                </marker>
            </defs>
            <path 
                id={id} 
                className="react-flow__edge-path" 
                d={edgePath} 
                markerEnd={`url(#small-arrow-${id})`}
                style={edgeStyle} 
            />
            {!data?.extraAttributes?.virtualInitialEdge && !data?.extraAttributes?.virtualFinalEdge && (
                <EdgeLabelRenderer>
                    <Box 
                        style={{
                            position: 'absolute',
                            fontSize: 12,
                            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
                            pointerEvents: 'all',
                            padding: '5px',
                        }}
                    >
                        <Paper 
                            variant='outlined'
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1,
                                backgroundColor: 'white',
                                borderColor: '#e0e0e0',
                                borderRadius: '50px',
                                boxShadow: 1,
                                px: 2,
                                py: 0.5,
                            }}
                        >
                            {data?.Icon && (
                                <MuiIconDisplay
                                    iconName={data.Icon}
                                    sx={{
                                        fontSize: 18,
                                        color: data.DisplayColor || 'inherit'
                                    }}
                                />
                            )}
                            <Typography sx={{ fontSize: 12, m: 0 }}>
                                {data?.labelKey && intl.get(data?.labelKey) || data?.actionName}
                            </Typography>
                        </Paper>
                    </Box>
                </EdgeLabelRenderer>
            )}
        </>
    )
})
FloatingEdge.displayName = 'FloatingEdge';
export default FloatingEdge;